import { getChatResponse } from '../api';
import { AbortError } from '../custom-errors';
import { callFunction, FunctionError } from '../edge-functions';
import { getExternalAgentReply, isExternalAgent } from '../external-agents/api';
import type { Agent } from '../types';
import type {
  ConnectionCheckResult,
  ImportManagedAgentParams,
  ManagedAgentList,
  TurnCheck,
  WorkspaceKeyStatus,
} from './types';

export const MANAGED_AGENT_SOURCE = 'anthropic_managed';

/** How often a running agent turn is checked, and when we give up on it. */
const CHECK_INTERVAL_MS = 2_000;
const TURN_TIMEOUT_MS = 10 * 60_000;
const CHECK_TIMEOUT_MS = 2 * 60_000;

export const isManagedAgent = (agent?: Pick<Agent, 'source'> | null) =>
  agent?.source === MANAGED_AGENT_SOURCE;

/** `code` sent when Anthropic stopped the agent at its spend limit. */
export const SPEND_LIMIT_REACHED = 'spend_limit_reached';

export { FunctionError };

export const getAnthropicKeyStatus = (workspaceId: string) =>
  callFunction<WorkspaceKeyStatus>('workspace-api-key', {
    workspaceId,
    action: 'status',
  });

export const saveAnthropicKey = (workspaceId: string, apiKey: string) =>
  callFunction<WorkspaceKeyStatus>('workspace-api-key', {
    workspaceId,
    action: 'save',
    apiKey,
  });

export const removeAnthropicKey = (workspaceId: string) =>
  callFunction<WorkspaceKeyStatus>('workspace-api-key', {
    workspaceId,
    action: 'remove',
  });

export const listManagedAgents = (workspaceId: string) =>
  callFunction<ManagedAgentList>('managed-agents', {
    workspaceId,
    action: 'list',
  });

export const importManagedAgent = ({
  workspaceId,
  ...params
}: ImportManagedAgentParams) =>
  callFunction<{ agentId: string; name: string; version: number }>(
    'managed-agents',
    { workspaceId, action: 'import', ...params },
  );

/** The spend limit new simulations of an imported agent start with. */
export const updateManagedAgentBudget = (
  workspaceId: string,
  agentId: string,
  budgetCents: number,
) =>
  callFunction<{ budgetCents: number }>('managed-agents', {
    workspaceId,
    action: 'update-budget',
    agentId,
    budgetCents,
  });

/**
 * Raises the spend limit of a simulation that hit it. Anthropic resumes the
 * paused turn; continuing the simulation picks the reply up.
 */
export const raiseSimulationSpendLimit = (
  workspaceId: string,
  simulationId: string,
  budgetCents: number,
) =>
  callFunction<{ budgetCents: number }>('managed-agent-turn', {
    workspaceId,
    simulationId,
    action: 'raise-budget',
    budgetCents,
  });

export const updateManagedAgentVersion = (
  workspaceId: string,
  agentId: string,
) =>
  callFunction<{ version: number }>('managed-agents', {
    workspaceId,
    action: 'update-version',
    agentId,
  });

/**
 * Sends one hello to an agent in a throwaway session and waits for its reply,
 * to prove the key, agent and environment work together before importing.
 */
export async function runConnectionCheck({
  workspaceId,
  anthropicAgentId,
  environmentId,
  agentId,
  signal,
}: {
  workspaceId: string;
  anthropicAgentId: string;
  environmentId: string;
  /** The imported agent, so a successful check marks it as working. */
  agentId?: string;
  signal?: AbortSignal;
}): Promise<ConnectionCheckResult> {
  const startedAt = Date.now();
  const { sessionId, eventId, message } = await callFunction<{
    sessionId: string;
    eventId: string;
    message: string;
  }>(
    'managed-agents',
    { workspaceId, action: 'check-start', anthropicAgentId, environmentId },
    signal,
  );

  const deadline = startedAt + CHECK_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await wait(CHECK_INTERVAL_MS, signal);
    const check = await callFunction<TurnCheck>(
      'managed-agents',
      { workspaceId, action: 'check-poll', sessionId, eventId, agentId },
      signal,
    );
    if (check.status === 'done') {
      return {
        sent: message,
        reply: check.content,
        seconds: Math.round((Date.now() - startedAt) / 100) / 10,
      };
    }
    if (check.status === 'failed') {
      throw new FunctionError(check.error, check.code);
    }
  }
  throw new Error('The agent did not reply within 2 minutes.');
}

interface ManagedAgentReplyParams {
  workspaceId: string;
  simulationId: string;
  message: string;
  /** Position of `message` in the transcript; makes a retried send safe. */
  messageIndex: number;
  signal?: AbortSignal;
}

/**
 * Sends the persona's message to the imported agent, then checks every few
 * seconds until it replies. The agent may take minutes, so there is no single
 * long request for a gateway to cut off.
 */
export async function getManagedAgentReply({
  workspaceId,
  simulationId,
  message,
  messageIndex,
  signal,
}: ManagedAgentReplyParams) {
  const turn = { workspaceId, simulationId };
  const stop = () =>
    callFunction('managed-agent-turn', { ...turn, action: 'stop' }).catch(
      () => {},
    );

  try {
    const { eventId } = await callFunction<{ eventId: string }>(
      'managed-agent-turn',
      { ...turn, action: 'send', message, messageIndex },
      signal,
    );

    const deadline = Date.now() + TURN_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await wait(CHECK_INTERVAL_MS, signal);
      const check = await callFunction<TurnCheck>(
        'managed-agent-turn',
        { ...turn, action: 'check', eventId },
        signal,
      );
      if (check.status === 'done') {
        return {
          content: check.content,
          ...(check.toolCalls.length
            ? { metadata: { toolCalls: check.toolCalls } }
            : {}),
        };
      }
      if (check.status === 'failed') {
        throw new FunctionError(check.error, check.code);
      }
    }

    stop();
    throw new Error('The agent took longer than 10 minutes to reply.');
  } catch (error) {
    if (signal?.aborted) {
      stop();
      throw new AbortError();
    }
    throw error;
  }
}

interface AgentReplyParams {
  agent: Agent | undefined;
  message: string;
  history: Parameters<typeof getChatResponse>[2];
  workspaceId?: string;
  simulationId?: string | null;
  signal?: AbortSignal;
}

/** One agent reply: PromptSafe's own chat, Claude Console, or the customer's API. */
export async function getAgentReply({
  agent,
  message,
  history,
  workspaceId,
  simulationId,
  signal,
}: AgentReplyParams) {
  if (!agent) throw new Error('Agent not found');
  if (!isManagedAgent(agent) && !isExternalAgent(agent)) {
    return getChatResponse(agent.id, message, history, workspaceId, signal);
  }
  if (!workspaceId || !simulationId) {
    throw new Error('Run a simulation to talk to a connected agent.');
  }
  if (isExternalAgent(agent)) {
    return getExternalAgentReply({
      workspaceId,
      simulationId,
      message,
      // `message` follows the history in the transcript.
      messageIndex: history.length,
      history,
      signal,
    });
  }
  return getManagedAgentReply({
    workspaceId,
    simulationId,
    message,
    // `message` follows the history in the transcript.
    messageIndex: history.length,
    signal,
  });
}

function wait(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new AbortError());
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new AbortError());
      },
      { once: true },
    );
  });
}
